import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faPlay, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Attachment, getTextByLang } from "./Game";
import "./gallery.css";

interface GalleryProps {
    items: Attachment[];
    /** Height of each gallery tile in px. Width follows the image's natural aspect ratio. Defaults to 320. */
    height?: number;
    lang: string;
    rtl?: boolean;
}

const isVideo = (url: string) => /\.(mp4|webm|ogg|mov)$/i.test(url.split("?")[0]);
const TOLERANCE = 2; // px — sub-pixel rounding guard

export const Gallery = ({ items, height = 320, lang, rtl = false }: GalleryProps) => {
    // Disabled state is derived from the actual scroll position so it
    // correctly reflects whether there is more content to reveal —
    // regardless of whether the user got there by clicking or images
    // just happened to all fit in the viewport.
    const [atScrollStart, setAtScrollStart] = useState(true);
    const [atScrollEnd,   setAtScrollEnd]   = useState(false);
    const [lightbox, setLightbox] = useState<number | null>(null);

    const trackRef   = useRef<HTMLDivElement>(null);
    const itemRefs   = useRef<(HTMLDivElement | null)[]>([]);
    const touchStartX = useRef<number | null>(null);

    // Re-evaluate scroll boundaries.  Called on every scroll event and
    // whenever the track resizes (e.g. images finish loading).
    const checkBounds = useCallback(() => {
        const t = trackRef.current;
        if (!t) return;
        // RTL: scrollLeft is 0 at the right (start) and goes negative leftward.
        setAtScrollStart(rtl ? t.scrollLeft >= -TOLERANCE : t.scrollLeft <= TOLERANCE);

        const lastItem = itemRefs.current[items.length - 1];
        if (lastItem) {
            const trackRect = t.getBoundingClientRect();
            const lastRect  = lastItem.getBoundingClientRect();
            const isLastFullyVisible = rtl
                ? lastRect.left  >= trackRect.left  - TOLERANCE
                : lastRect.right <= trackRect.right + TOLERANCE;
            setAtScrollEnd(isLastFullyVisible);
        } else {
            setAtScrollEnd(t.scrollLeft + t.clientWidth >= t.scrollWidth - TOLERANCE);
        }
    }, [items.length, rtl]);

    useEffect(() => {
        const t = trackRef.current;
        if (!t) return;
        t.addEventListener("scroll", checkBounds, { passive: true });
        const ro = new ResizeObserver(checkBounds);
        ro.observe(t);
        // Observe each item so checkBounds re-runs when images finish loading
        // and items get their real widths (the track's outer size doesn't change).
        itemRefs.current.forEach(item => { if (item) ro.observe(item); });
        checkBounds(); // initial state
        return () => { t.removeEventListener("scroll", checkBounds); ro.disconnect(); };
    }, [checkBounds]);

    /** Scroll the track so item[index]'s leading edge sits at the track's leading edge. */
    const scrollToItem = useCallback((index: number) => {
        const track = trackRef.current;
        const item  = itemRefs.current[index];
        if (!track || !item) return;

        const tr = track.getBoundingClientRect();
        const ir = item.getBoundingClientRect();

        // delta = how far the item's leading edge is from the track's leading edge.
        // Adding it to scrollLeft moves the viewport so they align.
        // RTL note: scrollLeft is negative when scrolled left, so the delta must
        // also be negative to scroll left (ir.right - tr.right is negative when
        // the item is to the left of the track's right edge).
        const delta = rtl
            ? (ir.right - tr.right)  // RTL: leading edge = right side
            : (ir.left  - tr.left);  // LTR: leading edge = left  side

        track.scrollTo({ left: track.scrollLeft + delta, behavior: "smooth" });
    }, [rtl]);

    /**
     * Find the index of the item whose leading edge is closest to the
     * track's leading edge right now (= the item currently "at the front").
     */
    const getLeadingIndex = useCallback((): number => {
        const t = trackRef.current;
        if (!t) return 0;
        const trackEdge = rtl ? t.getBoundingClientRect().right : t.getBoundingClientRect().left;
        let bestIdx = 0, minDist = Infinity;
        itemRefs.current.forEach((item, i) => {
            if (!item) return;
            const ir = item.getBoundingClientRect();
            const dist = Math.abs((rtl ? ir.right : ir.left) - trackEdge);
            if (dist < minDist) { minDist = dist; bestIdx = i; }
        });
        return bestIdx;
    }, [rtl]);

    const prev = useCallback(() => {
        const t = trackRef.current;
        const leadingIdx  = getLeadingIndex();
        const leadingItem = itemRefs.current[leadingIdx];
        if (leadingItem && t) {
            const trackRect = t.getBoundingClientRect();
            const itemRect  = leadingItem.getBoundingClientRect();
            // If the leading item is partially cut off, scroll it into view first
            // rather than jumping to the item before it.
            const isPartiallyHidden = rtl
                ? itemRect.right > trackRect.right + TOLERANCE
                : itemRect.left  < trackRect.left  - TOLERANCE;
            scrollToItem(isPartiallyHidden ? leadingIdx : Math.max(0, leadingIdx - 1));
        } else {
            scrollToItem(Math.max(0, leadingIdx - 1));
        }
    }, [getLeadingIndex, scrollToItem, rtl]);

    const next = useCallback(() => {
        scrollToItem(Math.min(items.length - 1, getLeadingIndex() + 1));
    }, [getLeadingIndex, items.length, scrollToItem]);

    const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
    const onTouchEnd   = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 50) (delta > 0) !== rtl ? next() : prev();
        touchStartX.current = null;
    };

    useEffect(() => {
        if (lightbox === null) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape")      setLightbox(null);
            if (e.key === "ArrowLeft")   setLightbox(i => i !== null ? Math.max(0, i - 1) : null);
            if (e.key === "ArrowRight")  setLightbox(i => i !== null ? Math.min(items.length - 1, i + 1) : null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, items.length]);

    // Left button  = prev in LTR / next in RTL
    // Right button = next in LTR / prev in RTL
    const leftDisabled  = rtl ? atScrollEnd   : atScrollStart;
    const rightDisabled = rtl ? atScrollStart : atScrollEnd;

    const lightboxUrl     = lightbox !== null ? getTextByLang(items[lightbox].file, lang) : "";
    const lightboxIsVideo = isVideo(lightboxUrl);

    return (
        <>
            <div className="gallery-root" dir="ltr">
                <button
                    className="gallery-arrow"
                    onClick={rtl ? next : prev}
                    disabled={leftDisabled}
                    aria-label={rtl ? "Next" : "Previous"}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="gallery-track-wrapper" style={{ height }}>
                <div
                    ref={trackRef}
                    className="gallery-track"
                    dir={rtl ? "rtl" : "ltr"}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {items.map((item, i) => {
                        const url   = getTextByLang(item.file, lang);
                        const video = isVideo(url);
                        return (
                            <div
                                key={i}
                                ref={el => { itemRefs.current[i] = el; }}
                                className="gallery-item"
                                onClick={() => setLightbox(i)}
                            >
                                {video ? (
                                    <>
                                        <video
                                            src={url}
                                            className="gallery-media gallery-media--video"
                                            preload="metadata"
                                        />
                                        <div className="gallery-play-overlay">
                                            <div className="gallery-play-btn">
                                                <FontAwesomeIcon icon={faPlay} />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <img
                                        src={url}
                                        alt={getTextByLang(item.name, lang)}
                                        className="gallery-media"
                                        draggable={false}
                                    />
                                )}
                                {(getTextByLang(item.name, lang) || getTextByLang(item.description, lang)) && (
                                    <div className="gallery-caption">
                                        {getTextByLang(item.name, lang) && (
                                            <div className="gallery-caption__title">{getTextByLang(item.name, lang)}</div>
                                        )}
                                        {getTextByLang(item.description, lang) && (
                                            <div className="gallery-caption__desc">{getTextByLang(item.description, lang)}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                </div>{/* gallery-track-wrapper */}

                <button
                    className="gallery-arrow"
                    onClick={rtl ? prev : next}
                    disabled={rightDisabled}
                    aria-label={rtl ? "Previous" : "Next"}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>

            {lightbox !== null && (
                <div className="gallery-lightbox" onClick={() => setLightbox(null)}>
                    <button
                        className="gallery-lightbox-close"
                        onClick={() => setLightbox(null)}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>

                    {items.length > 1 && (
                        <>
                            <button
                                className="gallery-lightbox-arrow gallery-lightbox-arrow--prev"
                                onClick={e => { e.stopPropagation(); setLightbox(i => i !== null ? Math.max(0, i - 1) : null); }}
                                disabled={lightbox === 0}
                                aria-label="Previous"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button
                                className="gallery-lightbox-arrow gallery-lightbox-arrow--next"
                                onClick={e => { e.stopPropagation(); setLightbox(i => i !== null ? Math.min(items.length - 1, i + 1) : null); }}
                                disabled={lightbox === items.length - 1}
                                aria-label="Next"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </>
                    )}

                    <div className="gallery-lightbox-content" onClick={e => e.stopPropagation()}>
                        {lightboxIsVideo ? (
                            <video
                                key={lightboxUrl}
                                src={lightboxUrl}
                                controls
                                autoPlay
                                className="gallery-lightbox-media"
                            />
                        ) : (
                            <img src={lightboxUrl} alt={getTextByLang(items[lightbox].name, lang)} className="gallery-lightbox-media" />
                        )}
                    </div>
                    {(getTextByLang(items[lightbox].name, lang) || getTextByLang(items[lightbox].description, lang)) && (
                        <div className="gallery-lightbox-caption" onClick={e => e.stopPropagation()}>
                            {getTextByLang(items[lightbox].name, lang) && (
                                <div className="gallery-lightbox-caption__title">{getTextByLang(items[lightbox].name, lang)}</div>
                            )}
                            {getTextByLang(items[lightbox].description, lang) && (
                                <div className="gallery-lightbox-caption__desc">{getTextByLang(items[lightbox].description, lang)}</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
